import { Star, ExternalLink, MessageSquarePlus } from "lucide-react";
import { TID } from "@/data/site";

// Direct deep link to OUR Google Business Profile only (no competitors)
const GOOGLE_PROFILE_URL = "https://g.page/r/CZQ7oXVCLYyNEBE";

// Direct "Write a review" link for our specific business
const GOOGLE_WRITE_REVIEW_URL = "https://g.page/r/CZQ7oXVCLYyNEBE/review";

const RATING = 5.0;
const TOTAL_REVIEWS = 23;

const Reviews = () => {
  return (
    <section
      id="reviews"
      data-testid={TID.reviewsSection}
      className="section relative grain"
    >
      <div className="max-w-6xl mx-auto px-5 lg:px-10">
        <div className="max-w-3xl">
          <div className="font-display text-3xl md:text-4xl text-gold">
            Traveller reviews
          </div>
          <p className="mt-6 text-foreground/75 max-w-xl leading-relaxed">
            Verified, real feedback from travellers across the world — straight
            from our Google Business profile.
          </p>
        </div>

        {/* Google Reviews Showcase Card */}
        <div
          data-testid="google-reviews-card"
          className="mt-14 rounded-3xl glass p-8 lg:p-12 relative overflow-hidden"
        >
          {/* Background decorative glow */}
          <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-gold/10 blur-3xl" />

          <div className="relative flex flex-col lg:flex-row items-center lg:items-stretch gap-10 lg:gap-14">
            {/* LEFT: Rating block */}
            <div className="flex flex-col items-center text-center lg:items-start lg:text-left shrink-0">
              {/* Google G logo */}
              <div className="flex items-center gap-2 text-foreground/80">
                <svg
                  className="h-6 w-6"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.61z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                <span className="text-sm font-medium uppercase tracking-widest">
                  Google Reviews
                </span>
              </div>

              {/* Rating number */}
              <div className="mt-5 flex items-end gap-3">
                <div
                  data-testid="google-rating-value"
                  className="font-display text-6xl lg:text-7xl text-gold leading-none"
                >
                  {RATING.toFixed(1)}
                </div>
                <div className="text-foreground/60 text-sm pb-2">/ 5.0</div>
              </div>

              {/* Stars */}
              <div className="mt-3 flex gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className="h-6 w-6 fill-[#e6b75c] text-gold"
                    strokeWidth={1.5}
                  />
                ))}
              </div>

              {/* Total count */}
              <div
                data-testid="google-reviews-count"
                className="mt-3 text-foreground/75 text-sm"
              >
                Based on{" "}
                <span className="font-semibold text-foreground">
                  {TOTAL_REVIEWS}+ verified reviews
                </span>
              </div>
            </div>

            {/* Divider (desktop only) */}
            <div className="hidden lg:block w-px bg-white/10 self-stretch" />

            {/* RIGHT: Description + CTAs */}
            <div className="flex-1 flex flex-col justify-center">
              <h3 className="font-display text-2xl lg:text-3xl leading-snug">
                What travellers say about us on{" "}
                <span className="text-gold italic">Google</span>
              </h3>
              <p className="mt-4 text-foreground/75 leading-relaxed max-w-xl">
                Read every review from real passengers — punctuality,
                professional drivers, immaculate vehicles, and a stress-free
                arrival into Athens. Reviews are public on our Google Business
                profile.
              </p>

              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <a
                  href={GOOGLE_PROFILE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="view-google-reviews-btn"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold text-[#0a1830] px-7 py-4 font-semibold text-sm hover:bg-[#f0c878] transition-all shadow-lg"
                >
                  Read all {TOTAL_REVIEWS}+ reviews on Google
                  <ExternalLink className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                </a>
                <a
                  href={GOOGLE_WRITE_REVIEW_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="leave-google-review-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 bg-[#0a1830]/40 backdrop-blur-sm text-foreground px-7 py-4 font-semibold text-sm hover:border-gold hover:text-gold transition-colors"
                >
                  <MessageSquarePlus className="h-4 w-4" />
                  Leave a Google Review
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Reviews;
